import { CheckCircleOutlined, CloudUploadOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Col, message, Modal, Row, UploadProps, UploadFile, Progress } from "antd";
import Dragger from "antd/es/upload/Dragger";
import { useCallback, useMemo, useState } from "react";
import { SiMicrosoftexcel } from "react-icons/si";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { componentPalette } from "src/theme/component-palette";
import * as XLSX from "xlsx";
import { uploadVocResult } from "src/services/fugitive.service";
import { UploadFugitive } from "src/entity/fugitive/fugitive.entity";
import { profileStore } from "src/stores/profile-store";
import { useShallow } from "zustand/react/shallow";
import { fugitiveStore } from "src/stores/fugitive-store";
import { RolesNameEnum } from "src/entity/common/roles.enum";
import dayjs from "dayjs";

const messageError = {
  template: "Incorrect template. Please review and make corrections.",
  type: "Incorrect type file. Please review and make corrections.",
  size: `The file size exceeds the allowed limit. Please reduce the file size.`,
};

const headerExcel = [
  "No",
  "Series",
  "Area",
  "P&ID Number",
  "Equipment ID",
  "Equipment Type",
  "Substance",
  "Service",
  "%BZ",
  "%BD",
  "%EDC",
  "%VCM",
  "Cause Uncheck",
  "Check Date",
  "VOCs Max (ppm)",
  "Fixed Date",
  "Fixed Result (ppm)",
  "TVOCs (Kg/y)",
  "Status VOCs",
];

const allowedFormatCheckDate = [
  "DD/MM/YYYY",
  "D/M/YYYY",
  "YYYY-MM-DD",
  "YYYY-M-DD",
  "DD-MMM-YYYY",
  "DD MM YYYY",
  "DD MMM YYYY",
  "DD-MM-YYYY",
  "DD M YYYY",
  "DD-MM-YY",
  "DD-M-YYYY",
  "DD-M-YY",
];

const ModalUploadVocResult = (props: { isOpen: boolean; onClose: () => void }) => {
  const { isOpen, onClose } = props;

  const profile = profileStore(useShallow((state) => state.profile));

  const selectedYear = fugitiveStore(useShallow((state) => state.selectedYearVocResult));

  const [loading, setLoading] = useState(false);

  const fileSizeLimit = 100; // 100MB
  const fileNameTemplate = "Template of upload fugitive data.xlsx";

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [dataUpload, setDataUpload] = useState<UploadFugitive[]>([]);
  const [progressPercent, setProgressPercent] = useState(0);

  const fileError = useMemo(() => {
    return fileList.some((file) => file.status === "error");
  }, [fileList]);

  // Voc Result Store
  const getVocResultStore = fugitiveStore(useShallow((state) => state.getVocResult));

  const companyId = useMemo(() => {
    if (profile?.activeCompany?._id) {
      return profile.activeCompany._id;
    }
    return undefined;
  }, [profile]);

  const beforeUpload: UploadProps["beforeUpload"] = (file: any) => {
    if (fileList.length >= 1) {
      setFileList([file]);
      message.info("Only one file is allowed. The new file has replaced the old one.");
    } else {
      setFileList([file]);
    }
  };

  const handleUploadChange: UploadProps["onChange"] = async (info) => {
    const file = info.file as UploadFile;
    const newFileList = [...info.fileList];

    // Limit to 1 file
    if (newFileList.length > 1) {
      newFileList.splice(0, newFileList.length - 1); // keep latest only
    }

    try {
      const isExcel =
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls");

      if (!isExcel) {
        file.status = "error";
        file.response = {
          message: messageError.type,
          rows: [],
        };
      } else if (file && typeof file.size === "number" && file.size / 1024 / 1024 >= fileSizeLimit) {
        file.status = "error";
        file.response = {
          message: messageError.size,
          rows: [],
        };
      } else {
        const data = file.originFileObj ? await file.originFileObj.arrayBuffer() : file;
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const expectedHeader = headerExcel;
        const fileHeader = json[0] as string[];
        const isHeaderValid = expectedHeader.every((key, i) => key === fileHeader[i]);
        if (!isHeaderValid) {
          file.status = "error";
          file.response = {
            message: messageError.template,
            rows: [],
          };
        } else {
          const rawData = XLSX.utils.sheet_to_json<Record<string, any>>(worksheet, {
            defval: "",
            raw: false,
          });

          const data: UploadFugitive[] = [];
          const validationErrors: { row: number; messages: string[] }[] = [];

          for (let index = 0; index < rawData.length; index++) {
            const item = rawData[index];
            const excelRow = index + 2; // row 1 = header
            const rowErrors: string[] = [];

            if (item["Check Date"]) {
              const rawDate = String(item["Check Date"]).trim();
              const parsedDate = dayjs(rawDate, allowedFormatCheckDate, true);
              if (!parsedDate.isValid()) {
                rowErrors.push(`Invalid Check Date format: ${rawDate}`);
              }
            }

            if (rowErrors.length > 0) {
              validationErrors.push({ row: excelRow - 1, messages: rowErrors });
            } else {
              data.push({
                areaStr: item["Area"],
                pidNumber: item["P&ID Number"],
                equipmentID: item["Equipment ID"],
                equipmentType: item["Equipment Type"],
                substance: item["Substance"],
                service: item["Service"],
                "%BZ": item["%BZ"],
                "%BD": item["%BD"],
                "%EDC": item["%EDC"],
                "%VCM": item["%VCM"],
                causeUncheck: item["Cause Uncheck"],
                checkdate: item["Check Date"],
                VOCmax: item["VOCs Max (ppm)"],
                fixedDate: item["Fixed Date"],
                fixedResult: item["Fixed Result (ppm)"],
                TVOC: item["TVOCs (Kg/y)"],
                statusVOC: item["Status VOCs"],
                series: item["Series"] || "",
              });
            }
          }

          if (validationErrors.length > 0) {
            file.status = "error";
            file.response = {
              message: `Found ${validationErrors.length} row(s) with error(s). Please review and correct.`,
              rows: validationErrors,
            };
          } else {
            file.status = "done";
            file.response = {
              message: "File uploaded successfully",
              rows: [],
            };
          }

          setDataUpload(data);
        }
      }
    } catch (error) {
      file.status = "error";
      file.response = {
        message: "An error occurred while reading the file.",
        rows: [],
      };
    }

    setFileList(newFileList);
  };

  const resetValues = () => {
    setFileList([]);
    setDataUpload([]);
    setProgressPercent(0);
    onClose();
  };

  const onUploadData = async () => {
    try {
      setLoading(true);
      setProgressPercent(0);

      const payload = {
        companyId,
        year: selectedYear,
        data: dataUpload,
      };
      const response = await uploadVocResult(payload, (event: ProgressEvent) => {
        if (event.lengthComputable) {
          const percentCompleted = Math.round((event.loaded * 100) / event.total);
          setProgressPercent(percentCompleted);
        }
      });

      if (response.data.code === 200) {
        setProgressPercent(100);
        message.success("Upload data successfully.");
      } else {
        message.error("Failed to upload data.");
        setProgressPercent(0);
      }
    } catch (error) {
      message.error("Failed to upload data.");
      setProgressPercent(0);
    } finally {
      resetValues();
      getVocResultStore(`companyId=${companyId}&year=${selectedYear}`);
      setLoading(false);
    }
  };

  const onDownloadTemplate = () => {
    try {
      const excelData = [headerExcel];
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Template");
      XLSX.writeFile(workbook, fileNameTemplate);
    } catch (error) {
      message.error("Failed to download template.");
    }
  };

  const uploadConfig = {
    multiple: false,
    accept: ".xls,.xlsx",
    maxCount: 1,
    showUploadList: false,
    listType: "picture" as const,
    customRequest: () => {},
    beforeUpload,
    onChange: handleUploadChange,
  };

  return (
    <div>
      <Modal
        centered
        title={<ModalTitle title="Upload Fugitive" />}
        open={isOpen}
        onCancel={() => resetValues()}
        width={700}
        okButtonProps={{ style: { display: "none" } }}
        cancelButtonProps={{ style: { display: "none" } }}
        styles={{
          body: {
            padding: "0px",
          },
        }}
        footer={
          <Row className="w-full border-t" justify={"center"}>
            <Button
              className="mt-4"
              key="submit"
              type="primary"
              disabled={fileList.length === 0 || fileError}
              onClick={onUploadData}
              loading={loading}
            >
              Upload
            </Button>
          </Row>
        }
      >
        <div className="w-full">
          <Dragger
            disabled={loading}
            {...uploadConfig}
            style={{
              width: "100%",
              margin: "0px",
            }}
          >
            <div className="flex flex-col items-center">
              <CloudUploadOutlined style={{ fontSize: "50px", color: "#9D9C9B" }} />
              <div className="font-medium text-center">
                <span style={{ color: "#9D9C9B" }}>Drag and Drop file here or Choose file</span>
              </div>
            </div>
          </Dragger>
        </div>

        <Row gutter={[16, 16]} className="pt-2 pb-2">
          <Col span={10} className="flex items-center gap-2">
            <SiMicrosoftexcel style={{ fontSize: "12px", color: componentPalette.excelColor }} />
            <Button onClick={onDownloadTemplate} type="link" className="p-0">
              {fileNameTemplate}
            </Button>
          </Col>
          <Col span={7} className="flex items-center">
            <span>• Only : xls, xlsx</span>
          </Col>
          <Col span={7} className="flex items-center">
            <span>• Maximum Size : 100 MB</span>
          </Col>
        </Row>

        {fileList.length > 0 && (
          <div
            style={{ width: "100%", marginLeft: "0px" }}
            className={"p-4 border rounded " + (fileError ? "bg-red-50 border-red-500" : "bg-gray-50 border-gray-200")}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {<SiMicrosoftexcel style={{ fontSize: "28px", color: componentPalette.excelColor }} />}
                <div className="ml-2">
                  <div className="text-sm font-medium text-gray-800">{fileList[0].name}</div>
                  {!fileError && (
                    <div className="text-xs text-gray-500">{fileList[0].size && (fileList[0].size / 1024 / 1024).toFixed(2)} MB</div>
                  )}
                </div>
              </div>
              <Button
                type="text"
                icon={<DeleteOutlined className="text-red-500" />}
                onClick={() => setFileList([])}
                className="hover:bg-red-100"
                disabled={loading}
              />
            </div>

            {loading && (
              <Progress
                percent={progressPercent}
                status={fileList[0]?.status === "error" ? "exception" : progressPercent === 100 ? "success" : "active"}
                format={(percent) => {
                  return percent === 100 ? (
                    <CheckCircleOutlined style={{ borderRadius: 7, fontSize: 14 }} />
                  ) : percent ? (
                    Math.round(percent) + "%"
                  ) : (
                    "0%"
                  );
                }}
              />
            )}

            {fileError && fileList[0].response ? (
              <div className="text-xs text-red-600 ml-8 mt-1 flex flex-col gap-1 max-h-48 overflow-y-auto">
                <div className="font-medium">{fileList[0].response.message}</div>
                {fileList[0].response.rows?.map((err: { row: number; messages: string[] }, idx: number) => (
                  <div key={`row-${err.row}-${idx}`} className="mt-1 pl-2 border-l-2 border-red-300">
                    <strong>Row {err.row}:</strong>
                    {err.messages.map((msg: string, msgIdx: number) => (
                      <div key={msgIdx}>{msg}</div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-green-600 ml-8 mt-1 flex flex-col gap-1">Verification successful. Please upload your file.</div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

const UploadVocResult = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const profile = profileStore(useShallow((state) => state.profile));

  const isCompanyAdmin = useMemo(() => {
    return profile?.roles && profile.roles.includes(RolesNameEnum.COMPANY_ADMIN);
  }, [profile]);

  return (
    <div>
      {isCompanyAdmin && (
        <>
          <Button
            type="text"
            icon={<CloudUploadOutlined style={{ fontSize: "28px", color: "#9D9C9B" }} onClick={() => setIsModalOpen(true)} />}
          />

          <ModalUploadVocResult isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </>
      )}
    </div>
  );
};

export default UploadVocResult;
